import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/context/AuthContext";
import { NotificationProvider } from "@/context/NotificationContext";
import { BulletinProvider } from "@/context/BulletinContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Schedule from "@/pages/Schedule";
import MyTours from "@/pages/MyTours";
import Reports from "@/pages/Reports";
import Inventory from "@/pages/Inventory";
import SupplyRequests from "@/pages/SupplyRequests";
import Documents from "@/pages/Documents";
import Supervisor from "@/pages/Supervisor";
import AuditTrail from "@/pages/AuditTrail";
import ScheduleTemplates from "@/pages/ScheduleTemplates";
import Admin from "@/pages/Admin";
import Inbox from "@/pages/Inbox";
import BulletinBoard from "@/pages/BulletinBoard";
import Compliance from "@/pages/Compliance";
import Validation from "@/pages/Validation";
import TradeBoard from "@/pages/TradeBoard";
import ChangePassword from "@/pages/ChangePassword";
import NewUser from "@/pages/NewUser";
import OnNow from "@/pages/OnNow";
import Roster from "@/pages/Roster";
import MemberProfile from "@/pages/MemberProfile";
import Training from "@/pages/Training";
import AdminReports from "@/pages/AdminReports";
import Applications from "@/pages/Applications";
import Settings from "@/pages/Settings";
import TriageLogin from "@/pages/triage/TriageLogin";
import TriageDashboard from "@/pages/triage/TriageDashboard";
import TriagePatientForm from "@/pages/triage/TriagePatientForm";
import TriagePatientList from "@/pages/triage/TriagePatientList";
import TriageCommandPost from "@/pages/triage/TriageCommandPost";
import TriageReports from "@/pages/triage/TriageReports";
import { hasLogisticsAccess } from "@/lib/permissions";
import { Toaster } from "@/components/ui/sonner";

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <NotificationProvider>
          <BulletinProvider>
          <BrowserRouter>
            <Routes>
              <Route path="/login" element={<Login />} />

              {/* BSVAC Event Triage — standalone tool with its own login
                  screen but the same underlying BSVAC accounts. Intended to
                  be reached via the triage.bsvacops.org subdomain (which
                  the reverse proxy rewrites to /triage). */}
              <Route path="/triage/login" element={<TriageLogin />} />
              <Route path="/triage" element={<ProtectedRoute><TriageDashboard /></ProtectedRoute>} />
              <Route path="/triage/patient/new" element={<ProtectedRoute><TriagePatientForm /></ProtectedRoute>} />
              <Route path="/triage/patient/:patientId/edit" element={<ProtectedRoute><TriagePatientForm /></ProtectedRoute>} />
              <Route path="/triage/patients" element={<ProtectedRoute><TriagePatientList /></ProtectedRoute>} />
              <Route path="/triage/command-post" element={<ProtectedRoute><TriageCommandPost /></ProtectedRoute>} />
              <Route path="/triage/reports" element={<ProtectedRoute><TriageReports /></ProtectedRoute>} />
              <Route path="/change-password" element={<ProtectedRoute><ChangePassword /></ProtectedRoute>} />
              <Route path="/" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
              <Route path="/schedule" element={<ProtectedRoute><Schedule /></ProtectedRoute>} />
              <Route path="/schedule/templates" element={<ProtectedRoute roles={["supervisor"]}><ScheduleTemplates /></ProtectedRoute>} />
              <Route path="/my-tours" element={<ProtectedRoute><MyTours /></ProtectedRoute>} />
              <Route path="/on-now" element={<ProtectedRoute><OnNow /></ProtectedRoute>} />
              <Route path="/roster" element={<ProtectedRoute><Roster /></ProtectedRoute>} />
              <Route path="/members/:id" element={<ProtectedRoute><MemberProfile /></ProtectedRoute>} />
              <Route path="/training" element={<ProtectedRoute><Training /></ProtectedRoute>} />
              <Route path="/admin/reports" element={<ProtectedRoute><AdminReports /></ProtectedRoute>} />
              <Route path="/admin/applications" element={<ProtectedRoute><Applications /></ProtectedRoute>} />
              <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
              <Route path="/trades" element={<ProtectedRoute><TradeBoard /></ProtectedRoute>} />
              <Route path="/users/new" element={<ProtectedRoute roles={["supervisor"]}><NewUser /></ProtectedRoute>} />
              <Route path="/reports" element={<ProtectedRoute roles={["supervisor"]}><Reports /></ProtectedRoute>} />
              <Route path="/inventory" element={<ProtectedRoute check={hasLogisticsAccess}><Inventory /></ProtectedRoute>} />
              <Route path="/supply-requests" element={<ProtectedRoute><SupplyRequests /></ProtectedRoute>} />
              <Route path="/documents" element={<ProtectedRoute><Documents /></ProtectedRoute>} />
              <Route path="/supervisor" element={<ProtectedRoute roles={["supervisor"]}><Supervisor /></ProtectedRoute>} />
              <Route path="/audit" element={<ProtectedRoute roles={["supervisor"]}><AuditTrail /></ProtectedRoute>} />
              <Route path="/inbox" element={<ProtectedRoute><Inbox /></ProtectedRoute>} />
              <Route path="/bulletins" element={<ProtectedRoute><BulletinBoard /></ProtectedRoute>} />
              <Route path="/compliance" element={<ProtectedRoute><Compliance /></ProtectedRoute>} />
              <Route path="/validation" element={
                <ProtectedRoute check={(u) => {
                  const extra = Array.isArray(u.additional_roles) ? u.additional_roles : [];
                  const owned = new Set([u.role, ...extra]);
                  return owned.has("commanding_officer") || owned.has("super_admin") || u.is_original_admin === true;
                }}>
                  <Validation />
                </ProtectedRoute>
              } />
              <Route path="/admin" element={<ProtectedRoute roles={["admin"]}><Admin /></ProtectedRoute>} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
          <Toaster position="top-right" richColors />
          </BulletinProvider>
        </NotificationProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
